import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Зберігає й відновлює позицію скролу вікна при переході список → деталь → назад.
 *
 * Чому не достатньо `display:none` overlay-патерну: коли список ховається,
 * документ коротшає і браузер ОДРАЗУ обрізає window.scrollY до 0. Прочитати
 * позицію в useEffect/useLayoutEffect вже пізно — там 0. Тому ловимо її в
 * момент кліку (`capture()` ДО setState, поки список ще на екрані), а
 * відновлюємо у useLayoutEffect коли деталь закрилась і список знову
 * display:block (документ виріс назад → scrollTo спрацює).
 *
 * Використання:
 *   const capture = useScrollMemory(!!selectedId);
 *   const open = (id) => { capture(); setSelectedId(id); };
 *   // onBack просто робить setSelectedId(null) — restore автоматичний.
 */
export function useScrollMemory(detailOpen: boolean): () => void {
  const y = useRef(0);
  const prev = useRef(detailOpen);

  const capture = useCallback(() => {
    y.current = window.scrollY || window.pageYOffset || 0;
  }, []);

  useLayoutEffect(() => {
    const was = prev.current;
    prev.current = detailOpen;
    if (was && !detailOpen) {
      // Деталь закрилась → список знову показано, документ виріс. Відновлюємо.
      window.scrollTo(0, y.current);
    } else if (!was && detailOpen) {
      // Зайшли в деталь → вона має починатись зверху.
      window.scrollTo(0, 0);
    }
  }, [detailOpen]);

  return capture;
}
